import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  FirestoreDataConverter,
  getDoc,
  getDocs,
  QueryDocumentSnapshot,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

import {
  AthletePosition,
  AthleteStatus,
  AthleteWithId,
  PerCoachCommentEntry,
  PerCoachCommentsValue,
  PhotoScoreValue,
  SkillFieldValue,
  SkillValue,
  SkillValues,
  VideoScoreValue,
} from '../types/athlete';
import { GENERAL_COMMENTS_FIELD_ID } from '../types/formSchema';
import { generateCommentId } from '../utils/ids';
import { isRecord } from '../utils/isRecord';
import {
  FormSchema,
  normalizeFormSchema,
} from '../types/formSchema';

const TRYOUTS_COLLECTION = 'tryouts';
const ATHLETES_SUBCOLLECTION = 'athletes';

function isValidPosition(val: unknown): val is AthletePosition {
  return typeof val === 'string' && val.trim().length > 0;
}

function isValidStatus(val: unknown): val is AthleteStatus {
  return val === null || (typeof val === 'string' && val.trim().length > 0);
}

export function parsePhotoScoreValue(val: unknown): PhotoScoreValue | null {
  if (!isRecord(val)) return null;
  if (typeof val.imageUrl === 'string' && val.imageUrl && typeof val.storagePathFull === 'string') {
    return {
      imageUrl: val.imageUrl,
      storagePathFull: val.storagePathFull,
      imageThumbnailUrl: typeof val.imageThumbnailUrl === 'string' && val.imageThumbnailUrl ? val.imageThumbnailUrl : undefined,
      storagePathThumb: typeof val.storagePathThumb === 'string' && val.storagePathThumb ? val.storagePathThumb : undefined,
    };
  }
  return null;
}

export function parseVideoScoreValue(val: unknown): VideoScoreValue | null {
  if (!isRecord(val)) return null;
  if (typeof val.videoUid === 'string' && val.videoUid) {
    return {
      videoUid: val.videoUid,
      uploadedBy: typeof val.uploadedBy === 'string' ? val.uploadedBy : '',
    };
  }
  return null;
}

export function parseSkillFieldValue(value: unknown): SkillFieldValue {
  if (typeof value === 'number') return { rating: value };
  const photoDirect = parsePhotoScoreValue(value);
  if (photoDirect) return { photo: photoDirect };
  if (!isRecord(value)) return {};
  const parsed: SkillFieldValue = {};
  if (typeof value.rating === 'number') parsed.rating = value.rating;
  if (Array.isArray(value.comments)) parsed.comments = parsePerCoachComments(value.comments);
  const photo = parsePhotoScoreValue(value.photo);
  if (photo) parsed.photo = photo;
  const video = parseVideoScoreValue(value.video);
  if (video) parsed.video = video;
  return parsed;
}

function readAthleteCreatedAt(val: unknown): Timestamp {
  return val instanceof Timestamp ? val : Timestamp.fromMillis(0);
}

export function parseAthleteScores(val: unknown): SkillValues {
  if (!isRecord(val)) return {};
  const result: SkillValues = {};
  for (const [fieldId, fieldVal] of Object.entries(val)) {
    if (fieldId === GENERAL_COMMENTS_FIELD_ID) {
      result[fieldId] = parsePerCoachComments(fieldVal);
      continue;
    }
    const parsed = parseSkillFieldValue(fieldVal);
    if (Object.keys(parsed).length > 0) {
      result[fieldId] = parsed;
    }
  }
  return result;
}

export function parseAthleteDoc(id: string, val: unknown): AthleteWithId | null {
  if (!isRecord(val)) return null;
  return {
    id,
    name: typeof val.name === 'string' ? val.name : '',
    number: typeof val.number === 'string' ? val.number : '',
    email: typeof val.email === 'string' && val.email ? val.email : undefined,
    position: isValidPosition(val.position) ? val.position.trim() : 'Unknown',
    returningstatus: isValidStatus(val.returningstatus)
      ? (val.returningstatus === null ? null : val.returningstatus.trim())
      : null,
    createdAt: readAthleteCreatedAt(val.createdAt),
    createdBy: typeof val.createdBy === 'string' ? val.createdBy : '',
    scores: parseAthleteScores(val.scores),
    teamPlacement: typeof val.teamPlacement === 'string' && val.teamPlacement ? val.teamPlacement : null,
    imageUrl: typeof val.imageUrl === 'string' && val.imageUrl ? val.imageUrl : undefined,
    imageThumbnailUrl:
      typeof val.imageThumbnailUrl === 'string' && val.imageThumbnailUrl
        ? val.imageThumbnailUrl
        : undefined,
  };
}

export const athleteConverter: FirestoreDataConverter<AthleteWithId> = {
  toFirestore: ({ id: _id, ...data }) => data,
  fromFirestore: (snap: QueryDocumentSnapshot) =>
    parseAthleteDoc(snap.id, snap.data()) ?? {
      id: snap.id,
      name: '',
      number: '',
      position: 'Unknown',
      returningstatus: null,
      createdAt: Timestamp.fromMillis(0),
      createdBy: '',
      scores: {},
    },
};

function readStringArrayField(data: Record<string, unknown>, key: string): string[] {
  const raw = data[key];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

export function parseTryoutMeta(data: unknown): {
  name: string;
  formSchema: FormSchema;
  athletePositions: string[];
  athleteStatuses: string[];
  teams: string[];
} {
  if (!isRecord(data)) return { name: '', formSchema: { groups: [] }, athletePositions: [], athleteStatuses: [], teams: [] };
  let formSchema: FormSchema = { groups: [] };
  if (isRecord(data.formSchema) && Array.isArray(data.formSchema.groups)) {
    formSchema = normalizeFormSchema({
      groups: data.formSchema.groups as FormSchema['groups'],
    });
  }
  return {
    name: typeof data.name === 'string' ? data.name : '',
    formSchema,
    athletePositions: readStringArrayField(data, 'athletePositions'),
    athleteStatuses: readStringArrayField(data, 'athleteStatuses'),
    teams: readStringArrayField(data, 'teams'),
  };
}

// ---------------------------------------------------------------------------
// Tryout data model for scoring coaches
// ---------------------------------------------------------------------------

export interface TryoutScoringData {
  id: string;
  name: string;
  formSchema: FormSchema;
  athletePositions: string[];
  athleteStatuses: string[];
  teams: string[];
  athletes: AthleteWithId[];
}

export interface TryoutAddAthleteContext {
  name: string;
  athletePositions: string[];
  athleteStatuses: string[];
}

// ---------------------------------------------------------------------------
// One-time fetch (tryout + athletes)
// ---------------------------------------------------------------------------

/**
 * Load tryout metadata and all athletes once. No live subscription.
 * Throws if the tryout document does not exist.
 */
export async function fetchTryoutForScoring(
  db: Firestore,
  tryoutId: string,
): Promise<TryoutScoringData> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  const athletesRef = collection(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION)
    .withConverter(athleteConverter);

  const [tryoutSnap, athletesSnap] = await Promise.all([
    getDoc(tryoutRef),
    getDocs(athletesRef),
  ]);

  if (!tryoutSnap.exists()) {
    throw new Error('Tryout not found.');
  }

  const meta = parseTryoutMeta(tryoutSnap.data());
  const athletes = athletesSnap.docs.map((d) => d.data());

  return { id: tryoutId, ...meta, athletes };
}


/**
 * Tryout document only — for add-athlete UI (no athletes query).
 */
export async function fetchTryoutAddAthleteContext(
  db: Firestore,
  tryoutId: string,
): Promise<TryoutAddAthleteContext> {
  const tryoutRef = doc(db, TRYOUTS_COLLECTION, tryoutId);
  const tryoutSnap = await getDoc(tryoutRef);

  if (!tryoutSnap.exists()) {
    throw new Error('Tryout not found.');
  }

  const meta = parseTryoutMeta(tryoutSnap.data());
  return {
    name: meta.name,
    athletePositions: meta.athletePositions,
    athleteStatuses: meta.athleteStatuses,
  };
}

// ---------------------------------------------------------------------------
// Write operations
// ---------------------------------------------------------------------------

/**
 * Add a new athlete as its own document in the athletes subcollection.
 * Firestore generates the document ID. Each athlete document is independent,
 * so concurrent adds never conflict.
 * Returns the generated athlete ID.
 */
export async function addAthlete(
  db: Firestore,
  tryoutId: string,
  data: {
    name: string;
    number: string;
    email?: string;
    position: string;
    returningstatus: AthleteStatus;
    createdBy: string;
    imageUrl?: string;
    imageThumbnailUrl?: string;
  },
): Promise<string> {
  const athletesRef = collection(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION);
  const docRef = await addDoc(athletesRef, {
    name: data.name,
    number: data.number,
    email: data.email ?? '',
    position: data.position,
    returningstatus: data.returningstatus,
    createdAt: serverTimestamp(),
    createdBy: data.createdBy,
    scores: {},
    ...(data.imageUrl && data.imageThumbnailUrl
      ? { imageUrl: data.imageUrl, imageThumbnailUrl: data.imageThumbnailUrl }
      : {}),
  });
  return docRef.id;
}

export async function updateAthlete(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  data: {
    name: string;
    number: string;
    email?: string;
    position: string;
    returningstatus: AthleteStatus;
    imageUrl?: string;
    imageThumbnailUrl?: string;
  },
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId);
  await updateDoc(athleteRef, {
    name: data.name,
    number: data.number,
    email: data.email ?? '',
    position: data.position,
    returningstatus: data.returningstatus,
    imageUrl: data.imageUrl ?? '',
    imageThumbnailUrl: data.imageThumbnailUrl ?? '',
  });
}

export async function deleteAthlete(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId);
  await deleteDoc(athleteRef);
}

export async function setAthleteTeamPlacement(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  teamPlacement: string | null,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId);
  await updateDoc(athleteRef, { teamPlacement });
}

export async function setAthleteScoreField(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  fieldId: string,
  value: SkillValue,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId);
  await updateDoc(athleteRef, {
    [`scores.${fieldId}`]: value,
  });
}

// ---------------------------------------------------------------------------
// Per-coach comment parsing
// ---------------------------------------------------------------------------

/** Parses a raw Firestore value into a PerCoachCommentsValue array. */
export function parsePerCoachComments(val: unknown): PerCoachCommentsValue {
  if (!Array.isArray(val)) return [];
  return val.reduce<PerCoachCommentsValue>((acc, item) => {
    if (!isRecord(item)) return acc;
    if (
      typeof item.text === 'string'
      && typeof item.coachName === 'string'
      && typeof item.coachUid === 'string'
      && item.createdAt instanceof Timestamp
    ) {
      acc.push({
        id: typeof item.id === 'string' && item.id ? item.id : generateCommentId(),
        text: item.text,
        coachName: item.coachName,
        coachUid: item.coachUid,
        createdAt: item.createdAt,
      });
    }
    return acc;
  }, []);
}

/**
 * Parses the `comments` array nested inside a raw skill field value object.
 * Returns [] if the field is not an object or has no `comments` key.
 */
export function parseSkillComments(val: unknown): PerCoachCommentsValue {
  if (!isRecord(val)) return [];
  return parsePerCoachComments(val.comments);
}

// ---------------------------------------------------------------------------
// General Comments (gc_general) write operations
// ---------------------------------------------------------------------------

/**
 * Appends a new comment to the athlete's scores[gc_general] array.
 * Uses a transaction to safely append without overwriting concurrent saves.
 */
export async function addGeneralComment(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  entry: Omit<PerCoachCommentEntry, 'createdAt' | 'id'>,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId)
    .withConverter(athleteConverter);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(athleteRef);
    if (!snap.exists()) throw new Error('Athlete not found.');
    const { scores } = snap.data();
    const existing = parsePerCoachComments(scores[GENERAL_COMMENTS_FIELD_ID]);
    const newEntry: PerCoachCommentEntry = {
      ...entry,
      id: generateCommentId(),
      createdAt: Timestamp.now(),
    };
    transaction.update(athleteRef, {
      [`scores.${GENERAL_COMMENTS_FIELD_ID}`]: [...existing, newEntry],
    });
  });
}

/** Updates the text of an existing general comment by id. */
export async function editGeneralComment(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  commentId: string,
  newText: string,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId)
    .withConverter(athleteConverter);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(athleteRef);
    if (!snap.exists()) throw new Error('Athlete not found.');
    const { scores } = snap.data();
    const existing = parsePerCoachComments(scores[GENERAL_COMMENTS_FIELD_ID]);
    const updated = existing.map((entry) =>
      entry.id === commentId ? { ...entry, text: newText } : entry,
    );
    transaction.update(athleteRef, {
      [`scores.${GENERAL_COMMENTS_FIELD_ID}`]: updated,
    });
  });
}

/** Removes a general comment by id. */
export async function deleteGeneralComment(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  commentId: string,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId)
    .withConverter(athleteConverter);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(athleteRef);
    if (!snap.exists()) throw new Error('Athlete not found.');
    const { scores } = snap.data();
    const existing = parsePerCoachComments(scores[GENERAL_COMMENTS_FIELD_ID]);
    const updated = existing.filter((entry) => entry.id !== commentId);
    transaction.update(athleteRef, {
      [`scores.${GENERAL_COMMENTS_FIELD_ID}`]: updated,
    });
  });
}

// ---------------------------------------------------------------------------
// Skill field comment write operations
// ---------------------------------------------------------------------------

/**
 * Appends a new per-coach comment to scores[fieldId].comments.
 * Uses a transaction so concurrent saves from different coaches never conflict.
 */
export async function addSkillComment(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  fieldId: string,
  entry: Omit<PerCoachCommentEntry, 'createdAt' | 'id'>,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId)
    .withConverter(athleteConverter);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(athleteRef);
    if (!snap.exists()) throw new Error('Athlete not found.');
    const { scores } = snap.data();
    const fieldRaw = scores[fieldId];
    const existing = parseSkillComments(fieldRaw);
    const fieldObj = isRecord(fieldRaw) ? fieldRaw : {};
    const newEntry: PerCoachCommentEntry = {
      ...entry,
      id: generateCommentId(),
      createdAt: Timestamp.now(),
    };
    transaction.update(athleteRef, {
      [`scores.${fieldId}`]: { ...fieldObj, comments: [...existing, newEntry] },
    });
  });
}

/** Updates the text of an existing skill field comment by id. */
export async function editSkillComment(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  fieldId: string,
  commentId: string,
  newText: string,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId)
    .withConverter(athleteConverter);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(athleteRef);
    if (!snap.exists()) throw new Error('Athlete not found.');
    const { scores } = snap.data();
    const fieldRaw = scores[fieldId];
    const existing = parseSkillComments(fieldRaw);
    const fieldObj = isRecord(fieldRaw) ? fieldRaw : {};
    const updated = existing.map((e) => e.id === commentId ? { ...e, text: newText } : e);
    transaction.update(athleteRef, {
      [`scores.${fieldId}`]: { ...fieldObj, comments: updated },
    });
  });
}

/** Removes a skill field comment by id. */
export async function deleteSkillComment(
  db: Firestore,
  tryoutId: string,
  athleteId: string,
  fieldId: string,
  commentId: string,
): Promise<void> {
  const athleteRef = doc(db, TRYOUTS_COLLECTION, tryoutId, ATHLETES_SUBCOLLECTION, athleteId)
    .withConverter(athleteConverter);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(athleteRef);
    if (!snap.exists()) throw new Error('Athlete not found.');
    const { scores } = snap.data();
    const fieldRaw = scores[fieldId];
    const existing = parseSkillComments(fieldRaw);
    const fieldObj = isRecord(fieldRaw) ? fieldRaw : {};
    const updated = existing.filter((e) => e.id !== commentId);
    transaction.update(athleteRef, {
      [`scores.${fieldId}`]: { ...fieldObj, comments: updated },
    });
  });
}
